import React, { useState, useEffect } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { AnalysisWorkspace } from './AnalysisWorkspace';
import { withBackend } from '../utils/api';

const SharedReportView = () => {
  // Support both /report/:hash path and legacy ?report=hash query param
  const pathMatch = window.location.pathname.match(/^\/report\/(.+)/);
  const urlParams = new URLSearchParams(window.location.search);
  const hash = pathMatch?.[1] || urlParams.get('report');

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hash) {
      fetchReport();
    }
  }, [hash]);

  const fetchReport = async () => {
    try {
      const response = await fetch(
        withBackend(`/api/reports/${hash}`)
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Report not found or has expired');
        }
        throw new Error('Failed to load report');
      }

      const data = await response.json();

      if (data.success) {
        setReport(data.report);
      } else {
        throw new Error(data.error || 'Failed to load report');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading shared report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Report Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
          >
            Analyze Your Own Presentation
          </button>
        </div>
      </div>
    );
  }

  // Create a mock project object for AnalysisWorkspace
  const mockProject = {
    id: `shared-${hash}`,
    name: report.presentation_name,
    client: `Read-only • ${report.view_count} views${report.expires_at ? ` • Expires ${formatDate(report.expires_at)}` : ''}`,
    uploadMetadata: {
      audienceInfo: {
        analysisFocus: 'both',
        expertiseLevel: 'beginner'
      }
    }
  };

  return (
    <AnalysisWorkspace
      project={mockProject}
      analysis={report.analysis_data}
      onBack={() => window.location.href = '/'}
      onAnalysisComplete={() => {}}
    />
  );
};

export default SharedReportView;
